import { GripHorizontal, MoreHorizontal } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";

import { cn } from "@/lib/utils";

import ToolbarContextMenu from "@/components/widget/editor/context-menu";
// Toolbar button imports
import Align from "@/components/widget/editor/toolbar-buttons/aligns";
import BackgroundColorPicker from "@/components/widget/editor/toolbar-buttons/background-color-picker";
import BackgroundImagePicker from "@/components/widget/editor/toolbar-buttons/background-image-picker";
import Bold from "@/components/widget/editor/toolbar-buttons/bold";
import Copy from "@/components/widget/editor/toolbar-buttons/copy";
import FontPicker from "@/components/widget/editor/toolbar-buttons/font-picker";
import FontSizePicker from "@/components/widget/editor/toolbar-buttons/font-size";
import HeadingPicker from "@/components/widget/editor/toolbar-buttons/heading-picker";
import InsertButton from "@/components/widget/editor/toolbar-buttons/insert-button";
import InsertChart from "@/components/widget/editor/toolbar-buttons/insert-chart";
import InsertColumns from "@/components/widget/editor/toolbar-buttons/insert-column";
import InsertHorizonLine from "@/components/widget/editor/toolbar-buttons/insert-horizon-line";
import InsertIcon from "@/components/widget/editor/toolbar-buttons/insert-icon";
import InsertImage from "@/components/widget/editor/toolbar-buttons/insert-image";
import InsertLink from "@/components/widget/editor/toolbar-buttons/insert-link";
import InsertShape from "@/components/widget/editor/toolbar-buttons/insert-shape";
import InsertTable from "@/components/widget/editor/toolbar-buttons/insert-table";
import Italic from "@/components/widget/editor/toolbar-buttons/italic";
import ListOrder from "@/components/widget/editor/toolbar-buttons/list-order";
import Orientation from "@/components/widget/editor/toolbar-buttons/orientation";
import PageMargin from "@/components/widget/editor/toolbar-buttons/page-margin";
import PageSize from "@/components/widget/editor/toolbar-buttons/page-size";
import Paste from "@/components/widget/editor/toolbar-buttons/paste";
import Strikethrough from "@/components/widget/editor/toolbar-buttons/strikethrough";
import TextColorPicker from "@/components/widget/editor/toolbar-buttons/text-color";
import TextHighlight from "@/components/widget/editor/toolbar-buttons/text-highlight";
import Underline from "@/components/widget/editor/toolbar-buttons/underline";

import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import HtmlTab from "@/pages/form/_components/editor/tabs/html";
import BorderColorPicker from "../editor/toolbar-buttons/background-border-picker";

export interface RichEditorProps {
  /** Initial HTML content */
  value?: string;
  /** Called on every content change */
  onChange?: (html: string) => void;
  /** Label shown above the editor */
  label?: string;
  /** Direction override (rtl | ltr) */
  dir?: "rtl" | "ltr";
  /** Additional className for the outer wrapper */
  className?: string;
  /** Height of the editable area */
  editorHeight?: string;
  /** Expose imperative handle to read innerHTML */
  editorRef?: React.RefObject<HTMLDivElement>;
  /** HTML draft content for the HTML tab */
  htmlDraft?: string;
  /** Callback when HTML is saved */
  onSaveHtml?: (html: string) => void;
  /** Callback when HTML is reset */
  onResetHtml?: () => void;
}

// ─── Responsive overflow container ────────────────────────────────────────────

interface OverflowToolbarProps {
  children: React.ReactNode;
}

/**
 * Measures available width and hides items that overflow into a "..." popover.
 */
const OverflowToolbar: React.FC<OverflowToolbarProps> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(
    React.Children.count(children)
  );

  const childArray = React.Children.toArray(children);

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth - 36; // reserve space for "..." button
    const items = Array.from(measure.children) as HTMLElement[];

    let total = 0;
    let count = 0;
    for (const item of items) {
      total += item.offsetWidth + 4; // 4px gap
      if (total > availableWidth) break;
      count++;
    }
    setVisibleCount(Math.max(1, count));
  }, []);

  useLayoutEffect(() => {
    recalculate();
  }, [recalculate]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => recalculate());
    ro.observe(container);
    return () => ro.disconnect();
  }, [recalculate]);

  const visible = childArray.slice(0, visibleCount);
  const hidden = childArray.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      className="relative flex w-full items-center gap-1 overflow-hidden"
    >
      {/* Hidden measurement clone */}
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute flex items-center gap-1"
        aria-hidden
      >
        {childArray}
      </div>

      {/* Visible items */}
      {visible}

      {/* Overflow popover */}
      {hidden.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-blue-50"
              title="بیشتر"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto max-w-xs p-2"
            align="end"
            side="bottom"
          >
            <div className="flex flex-wrap gap-1">{hidden}</div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

// ─── Tab content definitions ────────────────────────────────────────────────

const HomeTabContent: React.FC<{
  editorRef: React.RefObject<HTMLDivElement>;
}> = ({ editorRef }) => (
  <OverflowToolbar>
    <FontPicker editorRef={editorRef} />
    <HeadingPicker editorRef={editorRef} />
    <FontSizePicker editorRef={editorRef} />
    <Bold editorRef={editorRef} />
    <Italic editorRef={editorRef} />
    <Underline editorRef={editorRef} />
    <Strikethrough editorRef={editorRef} />
    <TextColorPicker editorRef={editorRef} />
    <TextHighlight editorRef={editorRef} />
    <Align editorRef={editorRef} />
    <ListOrder editorRef={editorRef} />
    <InsertHorizonLine editorRef={editorRef} />
    <Copy editorRef={editorRef} />
    <Paste editorRef={editorRef} />
  </OverflowToolbar>
);

const InsertTabContent: React.FC<{
  editorRef: React.RefObject<HTMLDivElement>;
}> = ({ editorRef }) => (
  <OverflowToolbar>
    <InsertTable editorRef={editorRef} />
    <InsertImage editorRef={editorRef} />
    <InsertIcon editorRef={editorRef} />
    <InsertShape editorRef={editorRef} />
    <InsertChart editorRef={editorRef} />
    <InsertLink editorRef={editorRef} />
    <InsertButton editorRef={editorRef} />
  </OverflowToolbar>
);

const DesignTabContent: React.FC<{
  editorRef: React.RefObject<HTMLDivElement>;
}> = ({ editorRef }) => (
  <OverflowToolbar>
    <BackgroundImagePicker editorRef={editorRef} />
    <BackgroundColorPicker editorRef={editorRef} />
    <BorderColorPicker editorRef={editorRef} />

  </OverflowToolbar>
);

const LayoutTabContent: React.FC<{
  editorRef: React.RefObject<HTMLDivElement>;
}> = ({ editorRef }) => (
  <OverflowToolbar>
    <PageMargin editorRef={editorRef} />
    <InsertColumns editorRef={editorRef} />
    <Orientation editorRef={editorRef} />
    <PageSize editorRef={editorRef} />
  </OverflowToolbar>
);

// ─── Main RichEditor component ───────────────────────────────────────────────

const RichEditor: React.FC<RichEditorProps> = ({
  value = "",
  onChange,
  label,
  dir,
  className,
  editorHeight = "200px",
  editorRef: externalRef,
  htmlDraft,
  onSaveHtml,
  onResetHtml
}) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const resolvedRef = (externalRef ??
    innerRef) as React.RefObject<HTMLDivElement>;
  const [activeTab, setActiveTab] = useState<
    "home" | "insert" | "design" | "layout" | "html"
  >("home");
  const [htmlValue, setHtmlValue] = useState<string>(value);
  const [rightClickedElement, setRightClickedElement] =
    useState<HTMLElement | null>(null);

  // Parse initial height from editorHeight prop (e.g. "200px" → 200)
  const initialHeight = parseInt(editorHeight, 10) || 200;
  const [areaHeight, setAreaHeight] = useState<number>(initialHeight);
  const dragStateRef = useRef<{ startY: number; startH: number } | null>(null);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragStateRef.current = { startY: e.clientY, startH: areaHeight };

      const onMouseMove = (ev: MouseEvent) => {
        if (!dragStateRef.current) return;
        const delta = ev.clientY - dragStateRef.current.startY;
        const newH = Math.max(80, dragStateRef.current.startH + delta);
        setAreaHeight(newH);
      };

      const onMouseUp = () => {
        dragStateRef.current = null;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [areaHeight]
  );

  // Sync initial value - runs only when value changes; resolvedRef is stable by design
  useEffect(() => {
    const el = resolvedRef.current;
    if (el && el.innerHTML !== value) {
      el.innerHTML = value;
    }
    setHtmlValue(value);
  }, [value, resolvedRef]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const nextHtml = e.currentTarget.innerHTML;
    setHtmlValue(nextHtml);
    onChange?.(nextHtml);
  };

  const handleHtmlChange = (nextHtml: string) => {
    setHtmlValue(nextHtml);
    const el = resolvedRef.current;
    if (el && el.innerHTML !== nextHtml) {
      el.innerHTML = nextHtml;
    }
    onChange?.(nextHtml);
  };

  useEffect(() => {
    if (activeTab === "html") return;
    const el = resolvedRef.current;
    if (el && el.innerHTML !== htmlValue) {
      el.innerHTML = htmlValue;
    }
  }, [activeTab, htmlValue, resolvedRef]);

  const handleTabChange = (tab: string) => {
    const nextTab = tab as "home" | "insert" | "design" | "layout" | "html";
    if (nextTab === "html") {
      setHtmlValue(resolvedRef.current?.innerHTML ?? htmlValue);
    }
    setActiveTab(nextTab);
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    setRightClickedElement(
      target.closest("i") ||
      target.closest("a") ||
      target.closest("table") ||
      target
    );
  };

  return (
    <div
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md",
        className
      )}
      dir={dir}
    >
      {label && (
        <div className="px-4 pt-3">
          <label className="text-base font-semibold text-gray-800">
            {label}
          </label>
        </div>
      )}

      {/* ── Tabs toolbar ── */}
      <Tabs defaultValue="home" onValueChange={handleTabChange}>
        {/* Tab triggers row */}
        <div className="flex items-center border-b border-gray-200 bg-[#D8E7F4] px-2 sm:px-4">
          <TabsList className="h-9 gap-0 bg-transparent p-0">
            {(
              [
                { value: "home", label: "Home" },
                { value: "insert", label: "Insert" },
                { value: "design", label: "Design" },
                { value: "layout", label: "Layout" },
                { value: "html", label: "Html" }
              ] as const
            ).map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="h-9 rounded-none border-b-2 border-transparent px-2 text-xs font-medium text-gray-700 transition-colors hover:text-gray-900 data-[state=active]:border-b-gray-900 data-[state=active]:bg-white data-[state=active]:text-gray-900 sm:px-3 sm:text-sm"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Tab content strip */}
        <div className="min-h-[44px] border-b border-gray-200 bg-white px-2 py-1 sm:px-4">
          <TabsContent value="home" className="mt-0">
            <HomeTabContent editorRef={resolvedRef} />
          </TabsContent>
          <TabsContent value="insert" className="mt-0">
            <InsertTabContent editorRef={resolvedRef} />
          </TabsContent>
          <TabsContent value="design" className="mt-0">
            <DesignTabContent editorRef={resolvedRef} />
          </TabsContent>
          <TabsContent value="layout" className="mt-0">
            <LayoutTabContent editorRef={resolvedRef} />
          </TabsContent>
          <TabsContent value="html">
            <HtmlTab
              editorRef={resolvedRef}
              htmlDraft={htmlValue || htmlDraft || ""}
              onSave={(html) => {
                handleHtmlChange(html);
                onSaveHtml?.(html);
              }}
              onReset={() => {
                const resetHtml = value || "";
                handleHtmlChange(resetHtml);
                onResetHtml?.();
              }}
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* ── Editable area ── */}
      {activeTab === "html" ? (
        <textarea
          value={htmlValue}
          onChange={(e) => handleHtmlChange(e.target.value)}
          className="overflow-auto bg-white p-3 font-mono text-sm outline-none"
          style={{ height: areaHeight }}
          spellCheck={false}
          dir="ltr"
        />
      ) : (
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div
              ref={resolvedRef}
              contentEditable
              suppressContentEditableWarning
              onInput={handleInput}
              onContextMenu={handleContextMenu}
              className="overflow-auto bg-white p-3 outline-none"
              style={{ height: areaHeight }}
            />
          </ContextMenuTrigger>
          {rightClickedElement && (
            <ToolbarContextMenu
              editorRef={resolvedRef}
              rightClickedElement={rightClickedElement}
            />
          )}
        </ContextMenu>
      )}

      {/* ── Resize handle ── */}
      <div
        onMouseDown={handleResizeMouseDown}
        className="flex cursor-row-resize items-center justify-center border-t border-gray-200 bg-gray-50 py-0.5 hover:bg-gray-100 active:bg-gray-200"
        title="برای تغییر ارتفاع بکشید"
      >
        <GripHorizontal size={14} className="text-gray-400" />
      </div>
    </div>
  );
};

export default RichEditor;
